import React from "react";

// Devuelve true si el color de fondo (#RRGGBB) es oscuro (para usar texto blanco).
function isDarkBg(hex) {
  if (!hex || typeof hex !== "string" || hex.length < 7) return false;
  const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) < 140;
}

// Renderiza fielmente la grilla parseada de un Excel (valores, merges, estilos).
export function ExcelGrid({ sheet }) {
  if (!sheet) return null;
  const cols = sheet.cols || [];
  const rows = sheet.rows || [];
  return (
    <div className="overflow-auto border border-slate-200 rounded-xl bg-white" data-testid="excel-grid">
      <table className="border-collapse text-[13px]" style={{ tableLayout: "fixed" }}>
        <colgroup>
          {cols.map((w, i) => (
            <col key={i} style={{ width: `${Math.max(40, Math.round((w || 10) * 7.5))}px` }} />
          ))}
        </colgroup>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} style={row.h ? { height: `${Math.round(row.h * 1.33)}px` } : undefined}>
              {row.cells.map((cell, ci) => {
                if (cell.cov) return null;
                const s = cell.s || {};
                const style = {
                  fontWeight: s.b ? 700 : 400,
                  fontStyle: s.i ? "italic" : "normal",
                  color: s.fg || (isDarkBg(s.bg) ? "#FFFFFF" : "#0f172a"),
                  backgroundColor: s.bg || "transparent",
                  textAlign: s.a || (cell.raw !== undefined && typeof cell.raw === "number" ? "right" : "left"),
                  fontSize: s.fs ? `${s.fs}px` : undefined,
                  borderTop: s.bt ? "1px solid #94a3b8" : "1px solid #f1f5f9",
                  borderBottom: s.bb ? "1px solid #94a3b8" : "1px solid #f1f5f9",
                  borderLeft: s.bl ? "1px solid #94a3b8" : "1px solid #f8fafc",
                  borderRight: s.br ? "1px solid #94a3b8" : "1px solid #f8fafc",
                  padding: "3px 8px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                };
                return (
                  <td
                    key={ci}
                    rowSpan={cell.rs || 1}
                    colSpan={cell.cs || 1}
                    style={style}
                    title={cell.v}
                  >
                    {cell.v}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SheetTabs({ sheets, active, onChange }) {
  if (!sheets || sheets.length <= 1) return null;
  return (
    <div className="flex items-center gap-1 flex-wrap" data-testid="excel-sheet-tabs">
      {sheets.map((sh, i) => (
        <button
          key={i}
          onClick={() => onChange(i)}
          data-testid={`excel-sheet-tab-${i}`}
          className={`px-3 h-8 rounded-lg text-sm font-semibold transition-colors ${
            active === i ? "bg-red-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          {sh.name}
        </button>
      ))}
    </div>
  );
}


// Exporta las hojas parseadas a un Excel (.xls HTML) conservando valores,
// celdas combinadas, colores de fondo/fuente y negritas.
export function exportSheetsToExcel(sheets, filename) {
  const esc = (v) => String(v == null ? "" : v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const tables = (sheets || []).map((sh) => {
    const rows = sh.rows.map((row) => {
      const tds = row.cells.map((c) => {
        if (c.cov) return "";
        const s = c.s || {};
        const st = [];
        if (s.bg) st.push(`background-color:${s.bg}`);
        if (s.fg) st.push(`color:${s.fg}`);
        else if (isDarkBg(s.bg)) st.push("color:#FFFFFF");
        if (s.b) st.push("font-weight:bold");
        if (s.i) st.push("font-style:italic");
        st.push(`text-align:${s.a || (typeof c.raw === "number" ? "right" : "left")}`);
        st.push("border:1px solid #cbd5e1;padding:2px 6px;mso-number-format:'\\@'");
        const span = `${c.rs ? ` rowspan="${c.rs}"` : ""}${c.cs ? ` colspan="${c.cs}"` : ""}`;
        return `<td${span} style="${st.join(';')}">${esc(c.v)}</td>`;
      }).join("");
      return `<tr>${tds}</tr>`;
    }).join("");
    return `<table border="1" style="border-collapse:collapse">${rows}</table>`;
  }).join("<br/>");
  const html = `\ufeff<html><head><meta charset="utf-8"></head><body>${tables}</body></html>`;
  const blob = new Blob([html], { type: "application/vnd.ms-excel" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  const clean = (filename || "resultado").replace(/[^\w\-]+/g, "_");
  a.download = `${clean}.xls`;
  a.click();
  URL.revokeObjectURL(a.href);
}
